import { Shield, Clock, FileText, Users, CheckCircle, Phone, AlertCircle, UserCheck, Church, Globe as GlobeIcon, MessageSquare, UsersRound } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import { useLanguage } from "@/hooks/useLanguage";
import { immigrationContent } from "@/i18n/immigration-content";
import AIReadabilityBlock from "@/components/AIReadabilityBlock";

const AsylumServices = () => {
  const { language } = useLanguage();
  const content = immigrationContent.asylum[language];

  const requirements = [
    "Persecution or well-founded fear of persecution",
    "Based on race, religion, nationality, political opinion, or membership in a particular social group",
    "Unable or unwilling to return to home country",
    "Apply within one year of arrival (with some exceptions)",
  ];

  const process = [
    { step: "1", title: "Initial Consultation", desc: "Evaluate your case and gather documentation", icon: <Users className="h-6 w-6" /> },
    { step: "2", title: "Prepare Application", desc: "File Form I-589 with USCIS", icon: <FileText className="h-6 w-6" /> },
    { step: "3", title: "Biometrics", desc: "Attend biometrics appointment", icon: <Shield className="h-6 w-6" /> },
    { step: "4", title: "Interview", desc: "Asylum interview with USCIS officer", icon: <Users className="h-6 w-6" /> },
    { step: "5", title: "Decision", desc: "Receive decision on your case", icon: <FileText className="h-6 w-6" /> },
  ];

  return (
    <div className="min-h-screen">
      <SEO />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center bg-gradient-to-br from-primary via-primary-light to-primary overflow-hidden">
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyem0wLThoMnYxMmgtMlYyNnptLTEyIDB2MTJoLTJWMjZoMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20" />
        
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="inline-block animate-fade-in">
            <Shield className="h-20 w-20 mx-auto mb-6 text-accent animate-pulse" />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">{content.title}</h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto animate-fade-in opacity-95">
            {content.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in">
            <Button asChild size="lg" variant="cta" className="hover-scale">
              <Link to="/booking">Schedule Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/20">
              <a href="tel:8329303059">
                <Phone className="mr-2 h-5 w-5" />
                Call Now: (832) 930-3059
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="bg-destructive text-white py-6 animate-fade-in">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg font-semibold mb-2">
            <AlertCircle className="inline h-5 w-5 mr-2 animate-pulse" />
            Facing Deportation or Detention? Call Immediately for Emergency Assistance
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="tel:8329303059" className="underline font-bold hover:text-accent">English: (832) 930-3059</a>
            <a href="tel:8323567254" className="underline font-bold hover:text-accent">Español: (832) 356-7254</a>
            <a href="tel:8329876543" className="underline font-bold hover:text-accent">Tiếng Việt: (832) 987-6543</a>
          </div>
        </div>
      </section>

      {/* What is Asylum */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">What is Asylum?</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-muted-foreground mb-6">
                Asylum is a form of protection granted to individuals who have suffered persecution or have a well-founded fear of persecution in their home country based on:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: UserCheck, text: "Race" },
                  { icon: Church, text: "Religion" },
                  { icon: GlobeIcon, text: "Nationality" },
                  { icon: MessageSquare, text: "Political Opinion" },
                  { icon: UsersRound, text: "Membership in Particular Social Group" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-md hover-scale">
                    <div className="h-12 w-12 rounded-full bg-gradient-gold-shine flex items-center justify-center shadow-gold flex-shrink-0">
                      <item.icon className="h-6 w-6 text-primary" />
                    </div>
                    <span className="font-semibold">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Asylum Eligibility Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {requirements.map((req, index) => (
                <div key={index} className="flex items-start gap-4 bg-card p-6 rounded-lg shadow-md hover-scale">
                  <CheckCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <span className="text-lg">{req}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">The Asylum Application Process</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            {process.map((item, index) => (
              <div key={index} className="flex gap-6 items-start bg-card p-8 rounded-lg shadow-lg hover-scale transition-all">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-gold-shine text-primary font-bold text-2xl flex items-center justify-center shadow-gold">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-accent">{item.icon}</div>
                    <h3 className="text-2xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-lg">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Contact */}
      <div className="container mx-auto px-4 py-12">
        <QuickContact 
          title="Need Help with Your Asylum Case?"
          description="Our experienced immigration attorneys are ready to help you navigate the asylum process."
        />
      </div>

      {/* Benefits */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Benefits of Asylum Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { icon: <Shield className="h-8 w-8" />, title: "Protection from Deportation", desc: "Legal status to remain in the United States" },
                { icon: <FileText className="h-8 w-8" />, title: "Work Authorization", desc: "Immediate eligibility to work in the U.S." },
                { icon: <Users className="h-8 w-8" />, title: "Family Reunification", desc: "Ability to petition for spouse and children" },
                { icon: <Clock className="h-8 w-8" />, title: "Path to Green Card", desc: "Eligible to apply for permanent residence after one year" },
              ].map((benefit, index) => (
                <div key={index} className="bg-card p-6 rounded-lg shadow-lg hover-scale">
                  <div className="text-accent mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {content.faq.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg px-6 shadow-md">
                  <AccordionTrigger className="text-lg font-semibold hover:text-accent">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* AI Readability Block */}
      <AIReadabilityBlock
        serviceName="Asylum Services"
        serviceDescription="Expert asylum legal services in Houston, Texas. We help individuals fleeing persecution based on race, religion, nationality, political opinion, or membership in a particular social group."
        keyPoints={[
          "Protection for individuals fleeing persecution in their home country",
          "Asylum applications based on race, religion, nationality, political opinion, or social group",
          "Must apply within one year of arrival (with some exceptions)",
          "Work authorization available after asylum approval",
          "Path to green card after one year of asylum status",
          "Trilingual support: English, Spanish, Vietnamese"
        ]}
        faqs={[
          {
            question: "What are the requirements for asylum in the United States?",
            answer: "You must demonstrate persecution or a well-founded fear of persecution based on race, religion, nationality, political opinion, or membership in a particular social group. You must generally apply within one year of arriving in the U.S."
          },
          {
            question: "How long does the asylum process take?",
            answer: "Processing times vary significantly. Affirmative asylum cases with USCIS can take 6 months to several years. Defensive asylum cases in immigration court may take 1-3 years or longer depending on the court's backlog."
          },
          {
            question: "Can I work while my asylum case is pending?",
            answer: "You can apply for work authorization (EAD) 150 days after filing a complete asylum application. If granted, you can work legally while your case is pending."
          }
        ]}
      />

      <CTASection 
        title="Ready to Start Your Asylum Application?"
        description="Don't wait. The sooner you apply, the sooner you can find safety and security in the United States."
      />
      <Footer />
    </div>
  );
};

export default AsylumServices;
